/* eslint-disable import/no-cycle */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, {ReactElement, ReactNode} from 'react';
import {documentToReactComponents} from '@contentful/rich-text-react-renderer';
import {BLOCKS, INLINES, Document} from '@contentful/rich-text-types';
import {useIntl} from 'react-intl';
import {cx} from '@emotion/css';
import {GatsbyImage} from 'gatsby-plugin-image';

import getIdNameFromLabel from '@fo/root/contentful/getIdNameFromLabel';
import {fonts} from '@fo/shared/styles/variables';
import Headers, {HeaderType} from '@fo/designSystem/Text/Headers/Headers';
import Paragraph from '@fo/designSystem/Text/Paragraph/Paragraph';
import Button from '@fo/designSystem/Buttons/Button/Button';
import getAccordion from '@fo/root/contentful/getAccordion';
import DynamicLink from '@fo/components/DynamicLink/DynamicLink';
import {ContentfulContent} from '@fo/shared-types';
import EmbeddableAsset from '@fo/designSystem/EmbeddableAsset/EmbeddableAsset';
import CardLink from '@fo/designSystem/CardLink/CardLink';
import {getPageRoute} from '@fo/utils/routes/getPageRoute';
import {segmentEvents, track} from '@fo/shared-analytics/tracking/segment';
import {useRedirectToTide, ESource} from '@fo/shared-hooks/useRedirectToTide';

import addRefsToRichText from './addRefsToRichText';
import getCtaButtonVariant from './getCtaButtonVariant';

import styles from './RichText.styles';

interface ContentfulNode {
  content?: any;
  data?: any;
}

interface Props {
  className?: string;
  content?: ContentfulContent;
  isSkipQuoteFormAndRedirectToTide?: boolean;
}

function RichText({className, content, isSkipQuoteFormAndRedirectToTide}: Props): ReactElement | null {
  const {formatMessage} = useIntl();
  const {redirectToTide} = useRedirectToTide();

  if (!content) {
    return null;
  }

  const getValidatedUrl = (url: string) =>
    url.includes(getPageRoute('quoteForm.start').replace(/\//g, '')) && isSkipQuoteFormAndRedirectToTide
      ? // set as undefined so the Button's onClick will be used instead
        undefined
      : // use Contentful's button url
        url;

  const richTextWithRefs = addRefsToRichText(content) as Document;

  const options = {
    renderNode: {
      [BLOCKS.HEADING_2]: (node: ContentfulNode, children: ReactNode) => {
        const id = node.content[0].value;
        return (
          <Headers
            className={cx(styles.heading)}
            headerType={HeaderType.H2}
            alignment={fonts.alignment.LEFT}
            id={getIdNameFromLabel(id)}
          >
            {children}
          </Headers>
        );
      },
      [BLOCKS.HEADING_3]: (node: ContentfulNode, children: ReactNode) => (
        <Headers className={cx(styles.heading)} headerType={HeaderType.H3} alignment={fonts.alignment.LEFT}>
          {children}
        </Headers>
      ),
      [BLOCKS.HEADING_4]: (node: ContentfulNode, children: ReactNode) => (
        <Headers className={cx(styles.heading)} headerType={HeaderType.H4} alignment={fonts.alignment.LEFT}>
          {children}
        </Headers>
      ),
      [BLOCKS.PARAGRAPH]: (_node: ContentfulNode, children: ReactNode) => <Paragraph>{children}</Paragraph>,
      [BLOCKS.EMBEDDED_ASSET]: (node: ContentfulNode) => {
        if (node.data.target.sys && node.data.target.sys.file) {
          const {
            title,
            description,
            file: {url, isDownloadable},
            gatsbyImageData,
          } = node.data.target.sys;

          if (url) {
            if (isDownloadable) {
              return (
                <div className={cx(styles.downloadableAsset)}>
                  <CardLink
                    href={url}
                    label={title}
                    description={description}
                    className={cx(styles.downloadableAssetCard)}
                    forceAnchorLink
                    blank
                  />
                </div>
              );
            }
            // It's an image asset
            return (
              <div className={cx(styles.imageContainer)}>
                {gatsbyImageData && (
                  <GatsbyImage
                    className={cx(styles.image)}
                    loading="lazy"
                    image={gatsbyImageData}
                    alt={title}
                    data-testid="ui.richTextImage"
                  />
                )}
                {description && (
                  <Paragraph fontSize={fonts.size.S} italic className={cx(styles.imageDescription)}>
                    {description}
                  </Paragraph>
                )}
              </div>
            );
          }
        }
        return null;
      },
      [BLOCKS.EMBEDDED_ENTRY]: (node: ContentfulNode) => {
        if (node.data.target.sys.accordion) {
          return getAccordion({node, formatMessage, nestedClass: styles.nested, isSkipQuoteFormAndRedirectToTide});
        }

        if (node.data.target.sys.contentType?.sys?.id === 'ctaButtons') {
          const {title, linkUrl, variant} = node.data.target.sys;
          const isGetStartedUrl = linkUrl.includes(getPageRoute('quoteForm.start'));
          return (
            <Button
              variant={getCtaButtonVariant(variant)}
              href={getValidatedUrl(linkUrl)}
              ariaLabel={title}
              className={cx(styles.ctaButton)}
              onClick={() => {
                if (isGetStartedUrl) {
                  track(segmentEvents.ctas.getStarted, {
                    button_text: title,
                  });
                }
                if (isGetStartedUrl && isSkipQuoteFormAndRedirectToTide) {
                  redirectToTide({source: ESource.FROM_CTA_TO_GET_STARTED});
                }
              }}
            >
              {title}
            </Button>
          );
        }

        if (node.data.target.sys.file && node.data.target.sys.file.embedType) {
          return (
            <EmbeddableAsset
              title={node.data.target.sys.file.title}
              type={node.data.target.sys.file.embedType}
              url={node.data.target.sys.file.url}
            />
          );
        }

        return null;
      },
      [BLOCKS.QUOTE]: (_node: ContentfulNode, children: ReactNode) => (
        // @todo abstract this
        <div className={cx(styles.blockQuote)}>{children}</div>
      ),
      [BLOCKS.TABLE]: (_node: ContentfulNode, children: ReactNode) => (
        <table className={cx(styles.table)}>
          <tbody>{children}</tbody>
        </table>
      ),
      [INLINES.HYPERLINK]: (node: ContentfulNode, children: ReactNode) => {
        if (node.data.uri) {
          return (
            <DynamicLink
              href={getValidatedUrl(node.data.uri) ?? '#'}
              onClick={() => {
                if (isSkipQuoteFormAndRedirectToTide) {
                  redirectToTide({source: ESource.FROM_CTA_TO_GET_STARTED});
                }
              }}
            >
              {children}
            </DynamicLink>
          );
        }
        return null;
      },
    },
  };

  return <div className={cx(styles.container, className)}>{documentToReactComponents(richTextWithRefs, options)}</div>;
}

export default RichText;
